import { Option, Select } from '@material-tailwind/react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import * as pmtiles from 'pmtiles';
import { useEffect, useMemo, useRef, useState } from 'react';
import Datepicker, { DateValueType } from 'react-tailwindcss-datepicker';
import { toast } from 'react-toastify';
import { getDateInputValue } from '../lib/date';
import { SocketRPC, socketEmit } from '../lib/socket';
import useProducerLocations from '../store/producer-locations';
import useUser from '../store/user';

const PMTILES_URL = `${process.env.REACT_APP_RELAY_PMTILES_SERVER_URL}/world.pmtiles`;
const STYLE_URL = `${process.env.REACT_APP_RELAY_PMTILES_SERVER_URL}/styles/arctic.json`;

type Coordinate = {
	latitude: number;
	longitude: number;
	producerLocationKey: string;
};

const ConsumerHeatmapPage = () => {
	const { user } = useUser();
	const [dateRange, setDateRange] = useState<DateValueType>({
		startDate: getDateInputValue(7),
		endDate: getDateInputValue(0),
	});
	const { producerLocations } = useProducerLocations();
	const [producerLocationKey, setProducerLocationKey] = useState<string>();
	const [consumerCoordinates, setConsumerCoordinates] = useState<Coordinate[]>(
		[],
	);
	const [isMapLoaded, setIsMapLoaded] = useState(false);
	const [radius, setRadius] = useState(15);
	const [intensity, setIntensity] = useState(1);
	const { producerKey } = user || {};
	const mapContainer = useRef<HTMLDivElement>(null);
	const mapRef = useRef<maplibregl.Map | null>(null);
	const producerLocationMarkerRef = useRef<maplibregl.Marker | null>(null);

	useEffect(() => {
		if (!dateRange) return;
		socketEmit(SocketRPC.GetConsumerHeatmap, {
			user,
			startDate: dateRange.startDate,
			endDate: dateRange.endDate,
			producerKey,
		})
			.then(res => {
				if (res.customerData?.length > 0) {
					setConsumerCoordinates(res.customerData);
				}
			})
			.catch(err => {
				toast.error(`Error searching archived orders. Please try again`);
				console.error(err);
			});
	}, [dateRange]);

	useEffect(() => {
		if (!mapContainer.current) return;
		// Initialize PMTiles
		let protocol = new pmtiles.Protocol();
		maplibregl.addProtocol('pmtiles', protocol.tile);
		const p = new pmtiles.PMTiles(PMTILES_URL);
		protocol.add(p);
		mapRef.current = new maplibregl.Map({
			container: mapContainer.current,
			style: STYLE_URL,
			zoom: 11,
			minZoom: 10,
			maxZoom: 18,
		});
		const producerMarkerElement = document.createElement('div');
		producerMarkerElement.className = 'producer-marker';
		producerLocationMarkerRef.current = new maplibregl.Marker({
			element: producerMarkerElement,
		});
		mapRef.current.on('load', () => {
			setIsMapLoaded(true);
		});
	}, []);

	useEffect(() => {
		if (consumerCoordinates.length === 0 || !isMapLoaded) return;
		if (mapRef.current?.getLayer('consumers-heat') != null) {
			mapRef.current?.removeLayer('consumers-heat');
		}
		if (mapRef.current?.getSource('consumers') != null) {
			mapRef.current?.removeSource('consumers');
		}
		const filteredConsumerCoordinates = consumerCoordinates.filter(coord => {
			if (producerLocationKey) {
				return coord.producerLocationKey === producerLocationKey;
			}
			return true;
		});
		const consumerBounds = new maplibregl.LngLatBounds();
		for (const coord of filteredConsumerCoordinates.length === 0
			? consumerCoordinates
			: filteredConsumerCoordinates) {
			consumerBounds.extend([coord.longitude, coord.latitude]);
		}
		mapRef.current?.fitBounds(consumerBounds, {
			padding: 100,
			animate: producerLocationKey != null,
		});
		mapRef.current?.addSource('consumers', {
			type: 'geojson',
			data: {
				type: 'FeatureCollection',
				features: filteredConsumerCoordinates.map(coord => ({
					type: 'Feature',
					geometry: {
						type: 'Point',
						coordinates: [coord.longitude, coord.latitude],
					},
					properties: {},
				})),
			},
		});

		mapRef.current?.addLayer({
			id: 'consumers-heat',
			type: 'heatmap',
			source: 'consumers',
			maxzoom: 18,
			paint: {
				// Increase the heatmap color weight weight by zoom level
				// heatmap-intensity is a multiplier on top of heatmap-weight
				'heatmap-intensity': 1,
				// Color ramp for heatmap.  Domain is 0 (low) to 1 (high).
				// Begin color ramp at 0-stop with a 0-transparency color
				// to create a blur-like effect.
				'heatmap-color': [
					'interpolate',
					['linear'],
					['heatmap-density'],
					0,
					'rgba(33,102,172, 0)',
					0.2,
					'rgb(103,169,207)',
					0.4,
					'rgb(209,229,240)',
					0.6,
					'rgb(253,219,199)',
					0.8,
					'rgb(239,138,98)',
					1,
					'rgb(178,24,43)',
				],
				'heatmap-radius': 15,
			},
		});
	}, [consumerCoordinates, isMapLoaded, producerLocationKey]);

	useEffect(() => {
		if (!mapRef.current || mapRef.current.getLayer('consumers-heat') == null)
			return;
		mapRef.current.setPaintProperty('consumers-heat', 'heatmap-radius', radius);
	}, [radius]);

	useEffect(() => {
		if (!mapRef.current || mapRef.current.getLayer('consumers-heat') == null)
			return;
		mapRef.current.setPaintProperty(
			'consumers-heat',
			'heatmap-intensity',
			intensity,
		);
	}, [intensity]);

	useEffect(() => {
		if (!producerLocations || !mapRef.current) return;
		if (!producerLocationKey) {
			producerLocationMarkerRef.current?.remove();
			return;
		}
		const producerLocation = producerLocations.find(
			pl => pl.producerLocationKey === producerLocationKey,
		);
		if (!producerLocation) return;
		const location = producerLocation.location as {
			coordinates: { longitude: number; latitude: number };
		};
		producerLocationMarkerRef.current?.setLngLat([
			location.coordinates.longitude,
			location.coordinates.latitude,
		]);
		producerLocationMarkerRef.current?.addTo(mapRef.current);
	}, [producerLocations, producerLocationKey]);

	const filteredProducerLocations = useMemo(() => {
		const producerLocationKeysWithConsumer = new Set(
			consumerCoordinates.map(coord => coord.producerLocationKey),
		);
		return producerLocations?.filter(pl =>
			producerLocationKeysWithConsumer.has(pl.producerLocationKey),
		);
	}, [consumerCoordinates, producerLocations]);

	return (
		<div className="h-full flex flex-col">
			<header className="flex justify-between items-center p-3">
				<h1 className="text-xl font-semibold">Consumer Heatmap</h1>
				<div className="grid grid-cols-[20rem_20rem] gap-4">
					{filteredProducerLocations &&
						filteredProducerLocations.length > 1 && (
							<Select
								label="Location"
								value={producerLocationKey}
								onChange={setProducerLocationKey}
							>
								{[
									{ producerLocationKey: '', producerLocationName: 'All' },
									...filteredProducerLocations,
								].map(({ producerLocationKey, producerLocationName }) => (
									<Option key={producerLocationKey} value={producerLocationKey}>
										{producerLocationName}
									</Option>
								))}
							</Select>
						)}
					<Datepicker
						value={dateRange}
						onChange={setDateRange}
						placeholder="Enter desired date range"
					/>
				</div>
			</header>
			<div className="grow relative" ref={mapContainer}>
				<div className="absolute z-[10000] top-1 right-1 w-48 h-28 p-2 bg-white opacity-80">
					<label>
						Radius
						<div className="flex items-center gap-4">
							<input
								type="range"
								value={radius}
								onChange={e => setRadius(Number(e.target.value))}
								step={1}
								min={1}
								max={30}
							/>
							<span>{radius}</span>
						</div>
					</label>
					<label>
						Intensity
						<div className="flex items-center gap-4">
							<input
								type="range"
								value={intensity}
								onChange={e => setIntensity(Number(e.target.value))}
								step={0.1}
								min={1}
								max={2}
							/>
							<span>{intensity}</span>
						</div>
					</label>
				</div>
			</div>
		</div>
	);
};

export default ConsumerHeatmapPage;
